/**
 * PriorityPill — work-queue priority-tier indicator.
 *
 * Four tiers per the design system: critical (top of queue), high (high
 * conversion / value), normal (standard SLA window), low (watching). A solid
 * dot in the tier color plus an uppercase label. `tierForScore` maps a raw
 * 0–100 priority score to a tier when an explicit tier is not provided.
 */
import type { CSSProperties } from 'react';
import { PRIORITY_COLORS, type PriorityTier } from '../theme';

/** Map a 0–100 priority score to a tier (higher = more urgent). */
export function tierForScore(score: number | null | undefined): PriorityTier {
  if (score === null || score === undefined || Number.isNaN(score)) return 'low';
  if (score >= 80) return 'critical';
  if (score >= 60) return 'high';
  if (score >= 35) return 'normal';
  return 'low';
}

const LABELS: Record<PriorityTier, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

export interface PriorityPillProps {
  tier: PriorityTier;
  /** Render only the colored dot (compact table cells). */
  dotOnly?: boolean;
  style?: CSSProperties;
}

export function PriorityPill({ tier, dotOnly = false, style }: PriorityPillProps) {
  const color = PRIORITY_COLORS[tier];
  if (dotOnly) {
    return (
      <span
        aria-label={`${LABELS[tier]} priority`}
        title={`${LABELS[tier]} priority`}
        style={{ ...dot, background: color, ...style }}
      />
    );
  }
  return (
    <span style={{ ...pill, color, background: `${color}1A`, ...style }}>
      <span style={{ ...dot, background: color }} />
      {LABELS[tier]}
    </span>
  );
}

const dot: CSSProperties = {
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
};

const pill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 10px',
  fontSize: '0.73rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  borderRadius: 999,
};
